import { type WorkspaceText, workspaceTextFor } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
// The start screen's company feed and the template gallery (collaboration v1,
// PR 6 §3–4). "New at {company} this week" lists what the whole company can
// see — the server checks every row — grouped Today / Earlier this week, with
// one line above it when something needs you. A company with no published
// Skills also sees "Start from a template" (template-gallery.tsx).
import { useEffect, useState } from 'react';
import { useWorkspace } from './context';
import { workspaceApi } from './api';
import { forkAndOpen } from './chat-collaboration';
import { agentsApi, agentErrorMessage } from './agents/api';
import { WorkspaceIcon } from './icons';
import { useInbox } from './inbox-api';
import { useInboxKey } from './inbox';
import { groupFeedRows, livingApi, relativeTime } from './living-api';
import { ProjectWork, projectHomeId } from './projects/home';
import type { CompanyFeed, FeedRow } from './living-api';
import { TemplateGallery } from './template-gallery';
import { isNewAppCanvas } from './apps/new-app';
import { makingNewAgent } from './agents/new-agent';
import { NewAppStarters } from './apps/app-create';

// Skills and Agents import the gallery from here, as before.
export { TemplateGallery };

const VERBS = (uiText: WorkspaceText): Record<string, Record<string, string>> => ({
  result: { shared: uiText('shared the Result'), updated: uiText('updated the Result') },
  skill: { published: uiText('published the Skill') },
  fact: { approved: uiText('approved the fact'), added: uiText('added the fact') },
  meeting: { added: uiText('added the meeting') },
  knowledge_page: { published: uiText('published the page'), updated: uiText('updated the page') },
  chat: { shared: uiText('shared the chat') },
  agent: { published: uiText('published a version of'), ran: uiText('ran') },
});
const SECTIONS = (uiText: WorkspaceText): Record<string, string> => ({
  result: uiText('Library'),
  skill: uiText('Skills'),
  fact: uiText('Knowledge'),
  meeting: uiText('Meetings'),
  knowledge_page: uiText('Knowledge'),
  chat: uiText('Chat'),
  agent: uiText('Agents'),
});
// Actions use the same collaboration operations as the item’s own page.
const ACTION_LABELS = (uiText: WorkspaceText): Record<string, string> => ({
  use: uiText('Use'),
  open: uiText('Open'),
  run: uiText('Run'),
  fork: uiText('Fork'),
});

const initials = (name: string | null | undefined) =>
  (name ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

export function feedSentence(
  row: FeedRow,
  userId: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const who =
    row.actor?.userId && row.actor.userId === userId
      ? uiText('You')
      : (row.actor?.name ?? '').split(' ')[0] || uiText('Someone');
  // An agent's new version is named: "published version 4 of Morning price check".
  const verb =
    row.kind === 'agent' && row.verb === 'published' && row.version
      ? uiText('published version {version} of', { version: row.version })
      : (VERBS(uiText)[row.kind]?.[row.verb] ?? row.verb);
  return { who, verb, title: row.title };
}

function useFeedAction() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  return async (row: FeedRow) => {
    if (row.kind === 'skill') {
      const ownsNavigation = ws.beginNavigation();
      const skill = await workspaceApi.skill(row.id).catch(() => null);
      if (!ownsNavigation()) return;
      if (skill) ws.useSkill(skill);
      else ws.notify(uiText('That Skill could not be opened.'));
      return;
    }
    if (row.kind === 'chat' && row.action === 'fork')
      return forkAndOpen(ws, row.id, undefined, uiText);
    if (row.kind === 'agent' && row.action === 'run') {
      const ownsNavigation = ws.beginNavigation();
      try {
        const { threadId } = await agentsApi.run(row.id);
        if (ownsNavigation()) ws.openThread(threadId);
      } catch (failure) {
        if (ownsNavigation()) ws.notify(agentErrorMessage(failure));
      }
      return;
    }
    if (row.kind === 'chat') return ws.openThread(row.id);
    if (row.kind === 'result' || row.kind === 'meeting')
      return ws.openCanvas({ kind: row.kind, id: row.id });
    if (row.kind === 'knowledge_page') return ws.openCanvas({ kind: 'page', id: row.id });
    window.location.hash = row.href;
  };
}

function FeedRowView({ row }: { row: FeedRow }) {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  const act = useFeedAction();
  const { who, verb, title } = feedSentence(row, ws.user?.id, uiText);
  return (
    <div className="ws-feed-row">
      <span className="ws-feed-avatar" aria-hidden="true">
        {row.actor?.userId === ws.user?.id
          ? initials(ws.user?.displayName)
          : initials(row.actor?.name)}
      </span>
      <span className="ws-feed-text">
        <b>{who}</b> {verb}{' '}
        {row.kind === 'chat' ? (
          <button className="ws-feed-chat-title" onClick={() => ws.openThread(row.id)}>
            {title}
          </button>
        ) : (
          <b>{title}</b>
        )}
        <small>
          {SECTIONS(uiText)[row.kind]} · {relativeTime(row.at, undefined, uiText.locale)}
        </small>
      </span>
      {/* Quiet and revealed on hover or focus (always shown on touch), so the week reads
          as a list rather than a column of buttons (DESIGN-SPEC §6.16 chat home). */}
      <button
        type="button"
        className="pc-btn ws-feed-action"
        data-variant="quiet"
        onClick={() => void act(row)}
      >
        {ACTION_LABELS(uiText)[row.action]}
      </button>
    </div>
  );
}

function NeedsYouLine() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const { inbox } = useInbox(useInboxKey());
  const needs = inbox?.counts.needsYou ?? 0;
  if (!needs) return null;
  return (
    <div className="ws-need-line">
      <WorkspaceIcon name="inbox" size={15} />
      <span>
        {uiRich('{needs} · reviews and approvals', {
          needs: (
            <b>
              {uiText(needs === 1 ? '{count} thing needs you' : '{count} things need you', {
                count: needs,
              })}
            </b>
          ),
        })}
      </span>
      <button className="ws-text-button" onClick={() => ws.navigate('inbox')}>
        {uiText('Open Inbox')}
      </button>
    </div>
  );
}

function FeedList({ feed }: { feed: CompanyFeed }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const groups = groupFeedRows(feed.rows, undefined, uiText.locale);
  return (
    <div className="ws-feed">
      <h2>{uiRich('New at {value1} this week', { value1: ws.companyName })}</h2>
      {groups.map((group) => (
        <div key={group.label}>
          <div className="ws-feed-day">{group.label}</div>
          {group.rows.map((row) => (
            <FeedRowView key={`${row.kind}:${row.id}`} row={row} />
          ))}
        </div>
      ))}
      {!feed.rows.length && (
        <p className="ws-feed-empty">
          {uiText(
            'Nothing new this week. Shared work, published Skills and approved knowledge show up here.',
          )}
        </p>
      )}
    </div>
  );
}

function useCompanyFeed() {
  const ws = useWorkspace();
  const [feed, setFeed] = useState<CompanyFeed | null>(null);
  const userId = ws.user?.id;
  useEffect(() => {
    // Not before an account is committed: the account guard (and its pair) exists only for a signed-in
    // tab, and this component mounts while the session is still unresolved.
    if (!ws.user?.id) {
      setFeed(null);
      return;
    }
    let active = true;
    livingApi
      .feed()
      .then((value) => {
        if (active) setFeed(value);
      })
      .catch(() => {
        if (active) setFeed(null);
      });
    return () => {
      active = false;
    };
  }, [userId, ws.threadId]);
  return feed;
}

/**
 * Thread slot after the composer and the starters, on an empty thread only
 * (app.tsx mounts it in the company workspace). Not while authoring a Skill.
 */
export function WorkspaceWelcomeFooter() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const feed = useCompanyFeed();
  if (
    ws.turnContext.intent.mode === 'skill-author' ||
    makingNewAgent(ws.turnContext) ||
    ws.canvas?.kind === 'storefront'
  )
    return null;
  // A project's home shows the work in the project where the home shows the company's feed.
  if (projectHomeId(ws)) return <ProjectWork />;
  if (isNewAppCanvas(ws.canvas)) return <NewAppStarters />;
  const noPublishedSkills = !ws.loading && !ws.data.skills.some((skill) => skill.publishedRevision);
  return (
    <section
      className="ws-welcome-footer"
      aria-label={uiText('New at {value1}', { value1: ws.companyName })}
    >
      <NeedsYouLine />
      {feed && <FeedList feed={feed} />}
      {noPublishedSkills && <TemplateGallery limit={3} />}
    </section>
  );
}
